import { useId, useState } from 'react';
import { Text } from '@querodelivery/uni-react';
import { format } from '@querodelivery/qd-date-helper';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faChevronDown, faClock } from '@fortawesome/pro-regular-svg-icons';
import { motion } from 'motion/react';
import type { MissionChangeLog } from '../../types';
import { MissionChangeLogDiffList } from '../mission-change-log-diff-list/mission-change-log-diff-list';
import {
  ENUM_MISSION_CHANGE_LOG_ACTION,
  MISSION_CHANGE_LOG_ACTION_ICON,
  MISSION_CHANGE_LOG_ACTION_LABEL,
} from '../../constants';
import styles from './mission-change-log-item.module.scss';

const HEADER_ICON_SIZE = 12;

const accordionTransition = {
  duration: 0.25,
  ease: 'easeInOut' as const,
};

const BULLET_CLASS_NAME = {
  [ENUM_MISSION_CHANGE_LOG_ACTION.CREATED]: styles.create,
  [ENUM_MISSION_CHANGE_LOG_ACTION.UPDATED]: styles.update,
  [ENUM_MISSION_CHANGE_LOG_ACTION.CANCELED]: styles.delete,
};

type MissionChangeLogItemProps = {
  entry: MissionChangeLog;
  isLast: boolean;
};

export const MissionChangeLogItem = ({
  entry,
  isLast,
}: MissionChangeLogItemProps) => {
  const [isOpen, setIsOpen] = useState(true);
  const contentId = useId();
  const triggerId = useId();
  const hasChanges = entry.changes && entry.changes?.length > 0;

  return (
    <div className={styles.row}>
      <div className={styles.timeline}>
        <div
          className={[styles.bullet, BULLET_CLASS_NAME[entry.action]].join(' ')}
        >
          <FontAwesomeIcon
            icon={MISSION_CHANGE_LOG_ACTION_ICON[entry.action]}
            height={14}
          />
        </div>
        {!isLast && <div className={styles.line} />}
      </div>
      <div className={styles.container}>
        <button
          type="button"
          className={styles.headerContainer}
          onClick={() => setIsOpen(current => !current)}
          aria-expanded={isOpen}
          aria-controls={contentId}
          id={triggerId}
        >
          <div className={styles.headerMain}>
            <div className={styles.managerContainer}>
              <Text tone="base" fontSize="sm" fontWeight="bold" lineHeight="lg">
                {entry.actor.name}
              </Text>
              <Text
                tone="supporting"
                fontSize="xs"
                fontWeight="medium"
                lineHeight="lg"
              >
                {entry.actor.email}
              </Text>
            </div>
            <div className={styles.date}>
              <FontAwesomeIcon icon={faClock} height={HEADER_ICON_SIZE} />
              <span className={styles.dateText}>
                <Text
                  as="span"
                  tone="supporting"
                  fontSize="xs"
                  fontWeight="medium"
                  lineHeight="lg"
                >
                  {format(entry.createdAt, 'dd/MM/yyyy')}
                </Text>
                <Text
                  as="span"
                  tone="supporting"
                  fontSize="xs"
                  fontWeight="medium"
                  lineHeight="lg"
                >
                  {`às ${format(entry.createdAt, 'HH:mm')}`}
                </Text>
              </span>
            </div>
          </div>
          <motion.span
            className={styles.chevron}
            initial={false}
            animate={{ rotate: isOpen ? 180 : 0 }}
            transition={accordionTransition}
            aria-hidden
          >
            <FontAwesomeIcon icon={faChevronDown} height={HEADER_ICON_SIZE} />
          </motion.span>
        </button>
        <motion.div
          id={contentId}
          role="region"
          aria-labelledby={triggerId}
          className={styles.content}
          initial={false}
          animate={{
            height: isOpen ? 'auto' : 0,
            opacity: isOpen ? 1 : 0,
          }}
          transition={accordionTransition}
        >
          <Text
            className={styles.action}
            tone="base"
            fontWeight="bold"
            fontSize="sm"
            lineHeight="lg"
          >
            {MISSION_CHANGE_LOG_ACTION_LABEL[entry.action]}
          </Text>
          {hasChanges && (
            <div className={styles.contentInner}>
              <MissionChangeLogDiffList diffList={entry.changes || []} />
            </div>
          )}
        </motion.div>
      </div>
    </div>
  );
};
